import { useCallback, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { TaskCreate } from "@common/contracts/task.contract";
import {
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { PersistableView } from "@common/types/views/views";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { CounterpartiesTaskTable } from "@components/tasks/TaskBody/Counterparties/CounterpartiesTaskTable";
import { WORKSPACE_COUNTERPARTIES_FILTER_TABS } from "@components/tasks/TaskBody/Counterparties/counterpartiesTaskFilters";
import {
    parseCounterpartiesTaskView,
    toDirectoryFilters,
    withDirectoryScope,
} from "@components/tasks/TaskBody/Counterparties/counterpartiesTaskView";
import { withoutBulkActions } from "@components/tasks/TaskBody/shared/taskTableView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import styles from "./CreateTaskDrawer.module.scss";
import { TaskCreationEntityFilter } from "./TaskCreationEntityFilter";
import { isCounterpartiesSelectionView } from "./taskCreationPreviewRegistry";
import { useTaskCreationPreviewView } from "./useTaskCreationPreviewView";

interface TaskCreationCounterpartiesPreviewProps {
    viewConfig: PersistableView;
}

interface PreviewBodyProps {
    initialView: CounterpartyDirectoryView;
    isSelection: boolean;
}

const renderEmptyPreview = () => (
    <EmptyState header="No counterparties match this task" />
);

const PreviewBody = ({ initialView, isSelection }: PreviewBodyProps) => {
    const { view, commitFilters, handleFiltersChange, handleViewChange } =
        useTaskCreationPreviewView({
            initialView,
            viewSchema: CounterpartyDirectoryViewSchema,
        });
    const { values } = useFormikContext<TaskCreate>();
    const [localScope, setLocalScope] = useState<DirectoryFilters>(() =>
        toDirectoryFilters(initialView.filters),
    );
    const scope = useMemo(
        () => ({
            ...localScope,
            entityIds: values.entityIds?.length ? values.entityIds : undefined,
        }),
        [localScope, values.entityIds],
    );

    const handleScopeChange = useCallback(
        (nextScope: DirectoryFilters) => {
            setLocalScope(nextScope);
            commitFilters(withDirectoryScope(view.filters, nextScope));
        },
        [commitFilters, view.filters],
    );

    const availableFilters = useMemo(
        () => (isSelection ? [] : WORKSPACE_COUNTERPARTIES_FILTER_TABS),
        [isSelection],
    );

    return (
        <CounterpartiesTaskTable
            queryScopeKey="creation-preview"
            view={view}
            onViewChange={handleViewChange}
            onFiltersChange={handleFiltersChange}
            scope={scope}
            onScopeChange={handleScopeChange}
            availableFilters={availableFilters}
            defaultView={initialView}
            lockScope={isSelection}
            hideEntityFilter
            headerActions={
                isSelection ? undefined : <TaskCreationEntityFilter />
            }
            emptyView={renderEmptyPreview}
        />
    );
};

export const TaskCreationCounterpartiesPreview = ({
    viewConfig,
}: TaskCreationCounterpartiesPreviewProps) => {
    const initialView = useMemo(
        () => withoutBulkActions(parseCounterpartiesTaskView(viewConfig)),
        [viewConfig],
    );
    const isSelection = isCounterpartiesSelectionView(viewConfig);

    return (
        <div className={styles.previewPane}>
            <div className={styles.previewSurface}>
                <PreviewBody
                    initialView={initialView}
                    isSelection={isSelection}
                />
            </div>
        </div>
    );
};
